/**
 * B552 leg (c2) — @transact/observe: every web client reports its own
 * failures, and no request can hang forever.
 *
 * Framework-free on purpose: the React `ErrorBoundary` (the error FACE)
 * lives in @transact/ui by the UI law; this package is the plumbing every
 * app shares — the deadline on the wire, the breadcrumbs, the debug switch,
 * the reporter and the global handlers — with no runtime dependency.
 *
 * The reporter is the one piece that must never become the bug it exists to
 * catch: it never throws, it never loops (de-dupe + a 5-a-minute cap), and a
 * page with no known tenant sends nothing (the sink's contract names one).
 */

export const DEFAULT_DEADLINE_MS = 12_000;
export const API_TIMEOUT = "API_TIMEOUT";

/** The wire's answer never came inside the deadline — retryable by nature. */
export class DeadlineExceededError extends Error {
  readonly code = API_TIMEOUT;
  readonly retryable = true;
  readonly deadlineMs: number;
  constructor(deadlineMs: number) {
    super(`no answer within ${Math.round(deadlineMs / 1000)} s`);
    this.name = "DeadlineExceededError";
    this.deadlineMs = deadlineMs;
  }
}

export type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

/**
 * A fetch that gives up: past `deadlineMs` (12 s) the request is ABORTED and
 * the promise rejects with `DeadlineExceededError` (code API_TIMEOUT). A
 * caller's own signal still aborts it, and that abort stays the caller's.
 */
export function fetchWithDeadline(fetchImpl: FetchLike, options: { readonly deadlineMs?: number } = {}): FetchLike {
  const deadlineMs = options.deadlineMs ?? DEFAULT_DEADLINE_MS;
  return async (input, init) => {
    const controller = new AbortController();
    let timedOut = false;
    const own = init?.signal ?? undefined;
    const forward = (): void => controller.abort(own?.reason);
    if (own !== undefined) {
      if (own.aborted) controller.abort(own.reason);
      else own.addEventListener("abort", forward, { once: true });
    }
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, deadlineMs);
    try {
      return await fetchImpl(input, { ...init, signal: controller.signal });
    } catch (error) {
      if (timedOut) throw new DeadlineExceededError(deadlineMs);
      throw error;
    } finally {
      clearTimeout(timer);
      own?.removeEventListener("abort", forward);
    }
  };
}

export const CRUMB_MAX_CHARS = 400;

export interface Breadcrumbs {
  add(event: string): void;
  list(): readonly string[];
}

/** The last `limit` (20) route/op events — what the page was doing when it failed. */
export function createBreadcrumbs(limit = 20): Breadcrumbs {
  const crumbs: string[] = [];
  return {
    add(event) {
      crumbs.push(event.slice(0, CRUMB_MAX_CHARS));
      while (crumbs.length > limit) crumbs.shift();
    },
    list() {
      return [...crumbs];
    },
  };
}

/** `?debug=1` or `localStorage["transact.debug"] === "1"` — a storage that refuses reads as off. */
export function debugEnabled(location: { readonly search: string }, storage?: { getItem(key: string): string | null }): boolean {
  if (new URLSearchParams(location.search).get("debug") === "1") return true;
  try {
    return storage?.getItem("transact.debug") === "1";
  } catch {
    return false;
  }
}

/** The client.error.report fields (packages/contracts client-errors.ts) minus the tenant, which the sender adds. */
export type ClientErrorKind = "crash" | "unhandled" | "api" | "network" | "contract";
export interface ClientErrorPayload {
  readonly app: string;
  readonly kind: ClientErrorKind;
  readonly message: string;
  readonly appVersion?: string;
  readonly route?: string;
  readonly deviceId?: string;
  readonly stack?: string;
  readonly breadcrumbs?: readonly string[];
  readonly lastRequestId?: string;
  readonly userAgent?: string;
}

export interface ReportInput {
  readonly kind: ClientErrorKind;
  readonly message: string;
  readonly stack?: string | undefined;
  readonly lastRequestId?: string | undefined;
}

export interface Reporter {
  /** true = accepted for sending; false = de-duped or over the cap. Never throws. */
  report(input: ReportInput): boolean;
  /** The same gate, answering the sink's reference once it lands (undefined when gated, refused or unwired). */
  reportWithRef(input: ReportInput): Promise<string | undefined>;
}

export const REPORTS_PER_MINUTE = 5;
const MINUTE = 60_000;
const BOUND = { message: 2_000, stack: 16_000, route: 400, appVersion: 60, userAgent: 500, requestId: 80 } as const;
const clip = (value: string | undefined, max: number): string | undefined =>
  value === undefined || value.length === 0 ? undefined : value.slice(0, max);

export function createReporter(options: {
  readonly app: string;
  readonly appVersion?: string | undefined;
  readonly route?: (() => string | undefined) | undefined;
  readonly deviceId?: (() => string | undefined) | undefined;
  readonly userAgent?: string | undefined;
  readonly crumbs?: Breadcrumbs | undefined;
  readonly send: (payload: ClientErrorPayload) => Promise<unknown>;
  readonly now?: (() => number) | undefined;
  readonly debug?: boolean | undefined;
  /** The console seam `?debug=1` mirrors into (injected so tests stay quiet). */
  readonly mirror?: ((...args: unknown[]) => void) | undefined;
}): Reporter {
  const now = options.now ?? (() => Date.now());
  const recent: number[] = [];
  const seen = new Map<string, number>();
  const accept = (input: ReportInput): ClientErrorPayload | undefined => {
      try {
        const t = now();
        const key = `${input.kind}\u0000${input.message}`;
        const last = seen.get(key);
        if (last !== undefined && t - last < MINUTE) return undefined;
        while (recent.length > 0 && t - recent[0]! >= MINUTE) recent.shift();
        if (recent.length >= REPORTS_PER_MINUTE) return undefined;
        recent.push(t);
        seen.set(key, t);
        const payload: ClientErrorPayload = {
          app: options.app,
          kind: input.kind,
          message: clip(input.message, BOUND.message) ?? "(no message)",
          ...optional("appVersion", clip(options.appVersion, BOUND.appVersion)),
          ...optional("route", clip(safe(options.route), BOUND.route)),
          ...optional("deviceId", safe(options.deviceId)),
          ...optional("stack", clip(input.stack, BOUND.stack)),
          ...(options.crumbs !== undefined && options.crumbs.list().length > 0 ? { breadcrumbs: options.crumbs.list() } : {}),
          ...optional("lastRequestId", clip(input.lastRequestId, BOUND.requestId)),
          ...optional("userAgent", clip(options.userAgent, BOUND.userAgent)),
        };
        if (options.debug === true) options.mirror?.("[transact] client error reported", payload);
        return payload;
      } catch {
        return undefined;
      }
  };
  const dispatch = (payload: ClientErrorPayload): Promise<unknown> =>
    Promise.resolve()
      .then(() => options.send(payload))
      .catch(() => undefined);
  return {
    report(input) {
      const payload = accept(input);
      if (payload === undefined) return false;
      void dispatch(payload);
      return true;
    },
    async reportWithRef(input) {
      const payload = accept(input);
      if (payload === undefined) return undefined;
      const answer = await dispatch(payload);
      return typeof answer === "string" && answer.length > 0 ? answer : undefined;
    },
  };
}

function optional<K extends string>(key: K, value: string | undefined): { [P in K]?: string } {
  return (value === undefined ? {} : { [key]: value }) as { [P in K]?: string };
}

function safe(read: (() => string | undefined) | undefined): string | undefined {
  try {
    return read?.();
  } catch {
    return undefined;
  }
}

/** Anything thrown, as words: an Error keeps its name, anything else is serialised. */
export function describeThrown(thrown: unknown): { message: string; stack?: string } {
  if (thrown instanceof Error) {
    return { message: `${thrown.name}: ${thrown.message}`, ...(thrown.stack !== undefined ? { stack: thrown.stack } : {}) };
  }
  if (typeof thrown === "string") return { message: thrown };
  try {
    return { message: JSON.stringify(thrown) ?? String(thrown) };
  } catch {
    return { message: String(thrown) };
  }
}

/* `any` on purpose: the DOM's own listener signature (Window's overloads) must assign here. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyListener = (event: any) => void;
export interface EventTargetLike {
  addEventListener(type: string, listener: AnyListener): void;
  removeEventListener(type: string, listener: AnyListener): void;
}

/**
 * `error` + `unhandledrejection` on the window → the reporter, as
 * `unhandled` (the ErrorBoundary reports render crashes as `crash`).
 * Returns the uninstall.
 */
export function installGlobalHandlers(target: EventTargetLike, reporter: Reporter): () => void {
  const onError = (event: { readonly message?: string; readonly error?: unknown }): void => {
    const described = event.error !== undefined ? describeThrown(event.error) : { message: event.message ?? "script error" };
    reporter.report({ kind: "unhandled", message: described.message, stack: described.stack });
  };
  const onRejection = (event: { readonly reason?: unknown }): void => {
    const described = describeThrown(event.reason);
    reporter.report({ kind: "unhandled", message: described.message, stack: described.stack });
  };
  target.addEventListener("error", onError);
  target.addEventListener("unhandledrejection", onRejection);
  return () => {
    target.removeEventListener("error", onError);
    target.removeEventListener("unhandledrejection", onRejection);
  };
}

/**
 * The wire to `client.error.report` (PUBLIC — no bearer): the tenant the
 * page knows rides body + header; no tenant known, nothing leaves the page.
 * The report itself rides a deadline — a dead network must not stack sends.
 */
export function opsSender(options: {
  readonly baseUrl: string;
  readonly tenantId: () => string | undefined;
  readonly fetchImpl?: FetchLike | undefined;
}): (payload: ClientErrorPayload) => Promise<string | undefined> {
  const send = fetchWithDeadline(options.fetchImpl ?? ((input, init) => fetch(input, init)));
  return async (payload) => {
    const tenantId = options.tenantId();
    if (tenantId === undefined) return undefined;
    const response = await send(`${options.baseUrl}/ops/client.error.report`, {
      method: "POST",
      headers: { "content-type": "application/json", "x-transact-tenant": tenantId },
      body: JSON.stringify({ tenantId, ...payload }),
    });
    if (!response.ok) return undefined;
    try {
      const body = (await response.json()) as { reportId?: unknown };
      return typeof body.reportId === "string" ? body.reportId : undefined;
    } catch {
      return undefined;
    }
  };
}

/** Reports raised before the wire exists wait here — at most this many. */
export const PRE_WIRE_QUEUE = 5;

export interface Observer {
  /** main.tsx, before the first render: global handlers + the debug switch. Idempotent. */
  boot(options: {
    readonly app: string;
    readonly target: EventTargetLike;
    readonly location: { readonly search: string; readonly pathname?: string };
    readonly storage?: { getItem(key: string): string | null } | undefined;
    readonly userAgent?: string | undefined;
    readonly appVersion?: string | undefined;
    readonly mirror?: ((...args: unknown[]) => void) | undefined;
  }): void;
  /** The api client, once it knows where the API is and whose page this is. */
  wire(options: { readonly baseUrl: string; readonly tenantId: () => string | undefined; readonly fetchImpl?: FetchLike | undefined }): void;
  /** One route/op event (the last 20 ride every report). */
  crumb(event: string): void;
  /** A failure the app caught itself (an api wall). false before boot. */
  report(input: ReportInput): boolean;
  /** The ErrorBoundary's door: a render crash, answered with the sink's reference when it lands. */
  crash(error: unknown, componentStack: string | undefined): Promise<string | undefined>;
}

export function createObserver(): Observer {
  const crumbs = createBreadcrumbs();
  let reporter: Reporter | undefined;
  let sender: ((payload: ClientErrorPayload) => Promise<string | undefined>) | undefined;
  const waiting: ClientErrorPayload[] = [];
  const send = async (payload: ClientErrorPayload): Promise<string | undefined> => {
    if (sender === undefined) {
      if (waiting.length < PRE_WIRE_QUEUE) waiting.push(payload);
      return undefined;
    }
    return sender(payload);
  };
  return {
    boot(options) {
      if (reporter !== undefined) return;
      reporter = createReporter({
        app: options.app,
        appVersion: options.appVersion,
        route: () => options.location.pathname,
        userAgent: options.userAgent,
        crumbs,
        send,
        debug: debugEnabled(options.location, options.storage),
        mirror: options.mirror ?? ((...args: unknown[]) => console.warn(...args)),
      });
      installGlobalHandlers(options.target, reporter);
    },
    wire(options) {
      sender = opsSender(options);
      const flush = waiting.splice(0, waiting.length);
      for (const payload of flush) void sender(payload).catch(() => undefined);
    },
    crumb(event) {
      crumbs.add(event);
    },
    report(input) {
      return reporter === undefined ? false : reporter.report(input);
    },
    async crash(error, componentStack) {
      if (reporter === undefined) return undefined;
      const described = describeThrown(error);
      const stack = [described.stack, componentStack].filter((part): part is string => part !== undefined && part.length > 0).join("\n");
      return reporter.reportWithRef({ kind: "crash", message: described.message, stack: stack.length > 0 ? stack : undefined });
    },
  };
}

/** The page's one observer — main.tsx boots it, the api client wires it. */
export const observer: Observer = createObserver();
